import { useEffect, useState, type FunctionComponent } from 'react';
import type { VideoFieldsFragment } from '../../../__generated__/graphql-client-types';
import { NO_IMAGE_URL } from '../../../constants/home/general';
import { windowIsNotSmall } from '../../../helpers/breakpoints/breakpoint.helpers';
import { type CloudinaryOptions } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import type { NormalizedConstructImage } from '../../../helpers/construct/page-content.helper';
import { type HomePageAnimatedSplashContent } from '../../../types/construct.types';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import { EmbeddedVideo } from '../../common-components/embedded-video/embedded-video.component';
import { Link } from '../../common-components/link/link.component';
import { PictureImage } from '../../images/picture-image/picture-image.component';
import { ctaButtonPosition, ctaForegroundSize, splashAspectRatio } from '../animated-splash/animated-splash.css';
import { MainImage } from '../home-splash/main-image/main-image.component';
import { translateAlignmentToStyles } from '../home-splash/splash-helpers/splash.helpers';

const SPLASH_WIDTH = 1280;
const SPLASH_HEIGHT = 512;

type CallToActionContentProps = {
	callToAction: HomePageAnimatedSplashContent['fields'];
};

const CallToActionContent: FunctionComponent<CallToActionContentProps> = ({ callToAction }) => {
	const { ctaStyle, ctaSubtext, ctaTitle, buttonTracking, buttonText, link } = callToAction;
	const { linkUrl, trackingCode } = link;
	const resolvedForegroundTracking = buttonTracking ?? trackingCode ?? '';
	const ctaForegroundColor = ctaStyle === 'LIGHT' ? 'bg-theme-primary-lighter' : 'bg-theme-primary-darker';
	const ctaSubtextColor = ctaStyle === 'LIGHT' ? 'theme-black' : 'theme-white';
	const buttonStyle = ctaStyle === 'LIGHT' ? 'PRIMARY' : 'SECONDARY';

	return (
		<div className="flex flex-column items-center justify-center overflow-hidden w-full p-0 md:p-8 relative">
			<div className={`absolute z-1 ${ctaButtonPosition}`}>
				<div className="dn flex-l">
					<LinkButton buttonStyle={buttonStyle} size={'DEFAULT'} url={linkUrl} analyticsHook={trackingCode ?? ''}>
						{buttonText}
					</LinkButton>
				</div>
				<div className="flex dn-l">
					<LinkButton buttonStyle={buttonStyle} size={'SMALL'} url={linkUrl} analyticsHook={trackingCode ?? ''}>
						{buttonText}
					</LinkButton>
				</div>
			</div>
			<Link
				url={linkUrl}
				ariaLabel="foreground"
				analyticsHook={resolvedForegroundTracking}
				className="flex flex-column items-center overflow-hidden"
				automationHook="landing-page-section">
				<div className={`${ctaForegroundSize} ${ctaForegroundColor} rounded-2xl`}>
					<div className={`m-8 ${ctaSubtextColor}`}>
						<h3 className="mt-0 mb-2 text-2xl lg:text-3xl font-light uppercase" style={{ letterSpacing: '2px' }}>
							{ctaTitle}
						</h3>
						<p className="my-0 lg:text-lg">{ctaSubtext}</p>
					</div>
				</div>
			</Link>
		</div>
	);
};

const FALLBACK_IMAGE_OPTIONS: CloudinaryOptions = {
	width: SPLASH_WIDTH,
	height: SPLASH_HEIGHT,
	quality: 80,
	progressive: true,
	format: 'jpg',
	type: 'private'
};

export type AnimatedSplashProps = {
	image: NormalizedConstructImage;
	fallbackImage: NormalizedConstructImage;
	video?: VideoFieldsFragment | null;
	mobileImage?: NormalizedConstructImage;
	backgroundTracking?: string | null;
	animatedSplashCallToAction: HomePageAnimatedSplashContent['fields'];
};

export const AnimatedSplash: FunctionComponent<AnimatedSplashProps> = ({
	image,
	fallbackImage,
	video,
	mobileImage,
	backgroundTracking,
	animatedSplashCallToAction
}) => {
	const [showVideo, setShowVideo] = useState<boolean>(false);
	const { link, alignment } = animatedSplashCallToAction;
	const { linkUrl, trackingCode } = link;
	const resolvedBackgroundTracking = backgroundTracking ?? trackingCode ?? '';
	const hasNoImage = image.id === NO_IMAGE_URL;
	const dynamicCTAAlignmentStyle = translateAlignmentToStyles(alignment ?? '');

	useEffect(() => {
		const isNotSmall = windowIsNotSmall();
		if (isNotSmall) {
			setShowVideo(true);
		}
	}, []);

	return (
		<section className="flex flex-column items-center w-100 min-h-100 relative pointer bg-theme-white" data-testid="animated-splash">
			<div className="flex flex-column items-center overflow-hidden w-100 relative br4" style={{ maxHeight: SPLASH_HEIGHT }}>
				<div className="absolute dn flex-ns z-1" style={{ ...dynamicCTAAlignmentStyle }}>
					<CallToActionContent callToAction={animatedSplashCallToAction} />
				</div>
				<div className="absolute dn-ns flex z-1 mt6">
					<CallToActionContent callToAction={animatedSplashCallToAction} />
				</div>
				{hasNoImage ? (
					<div className={`relative w-100 ${splashAspectRatio}`}>
						<div className="absolute mh3 mh0-ns" style={{ inset: 0 }}>
							<Link url={linkUrl}>
								<PictureImage
									description={fallbackImage.description}
									publicID={fallbackImage.id}
									options={FALLBACK_IMAGE_OPTIONS}
									className="br4 max-h-100"
									loading="eager"
									imageProps={{ width: SPLASH_WIDTH, height: SPLASH_HEIGHT }}
									artDirectionVariations={
										mobileImage
											? [
													{
														mediaCondition: { maxWidth: 767 },
														options: { type: 'private' },
														publicID: mobileImage.id
													}
											  ]
											: []
									}
								/>
							</Link>
						</div>
						{showVideo && video?.id && (
							<div className="absolute" style={{ inset: 0 }} data-testid="splash-video">
								<Link url={linkUrl} className="w-100 h-100">
									<div className="disableClickEvents">
										<EmbeddedVideo video={video} shouldLoadEagerly={true} />
									</div>
								</Link>
							</div>
						)}
					</div>
				) : (
					<MainImage
						background={image}
						url={linkUrl}
						analyticsHook={resolvedBackgroundTracking}
						options={{ ...FALLBACK_IMAGE_OPTIONS, format: 'auto' }}
					/>
				)}
			</div>
		</section>
	);
};
